import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { forceCollide } from 'd3-force-3d';
import { Api } from '../lib/api.js';
import { useWallet, useApi } from '../lib/wallet.jsx';
import { Button, ErrorState } from '../components/ui.jsx';
import { JourneySteps, JourneyNext } from '../components/Journey.jsx';

/**
 * Map me.
 *
 * A one-click knowledge map. The prompt below is pre-filled; the person edits
 * it or presses the button as-is. The API turns the description into a graph of
 * {nodes, links} which renders as a live, draggable force-directed map — drag
 * one bubble and the rest settle around it.
 *
 * The map is generated by a real model call server-side (§8.5: the browser
 * never sees a provider key). It works for a guest session; nothing here is
 * on-chain, so no wallet is required to explore.
 */

const DEFAULT_PROMPT =
  'I lead product at a fintech startup. I work most closely with my manager and' +
  ' two engineers, and I partner with the design and marketing teams. Right now' +
  ' I am focused on shipping a new mobile app and growing our user base. Outside' +
  ' work I run, cook, and spend time with family. This year I want to grow into a' +
  ' leadership role and stay healthy while doing it.';

// One-tap starting points. Short, natural, first-person descriptions anyone can
// relate to and edit. Tapping one fills the prompt.
const EXAMPLE_PROMPTS = [
  {
    label: 'Student',
    text:
      'I am a computer science student. This term I am taking algorithms,' +
      ' databases, and web development, and I have a group project due soon. I am' +
      ' in the coding club and play sports on the side. I want to land a summer' +
      ' internship and graduate with strong grades.',
  },
  {
    label: 'Professional',
    text:
      'I am a marketing manager at a consumer brand. I run our social and email' +
      ' campaigns, manage two teammates, and work with the sales team and an' +
      ' outside agency. This quarter I am launching a new product and growing our' +
      ' audience, and I am studying for an MBA on the side.',
  },
  {
    label: 'Business owner',
    text:
      'I own a coffee shop. I have a small team, work with local suppliers, and' +
      ' serve a regular crowd of students and remote workers. I handle the books' +
      ' and marketing myself. This year I want to open a second location and grow' +
      ' revenue.',
  },
  {
    label: 'Creator',
    text:
      'I make videos for a living. I run a weekly channel, edit for a few clients,' +
      ' and work with a co-host and an assistant. My income comes from ads,' +
      ' sponsorships, and freelance work. I want to grow my audience and launch a' +
      ' paid course this year.',
  },
];

// Each group maps to a brand-consistent accent. No cyan/teal anywhere.
const GROUP_COLORS = {
  you: '#F7F4FF',
  company: '#7C5CFF',
  product: '#9B85FF',
  person: '#8B70FF',
  market: '#6F4CF2',
  partner: '#B9A6FF',
  tech: '#5A3FD6',
  default: '#6F5C8D',
};

const colorFor = (group) => GROUP_COLORS[String(group ?? '').toLowerCase()] ?? GROUP_COLORS.default;

export function MapMe({ pushToast }) {
  const { sessionReady, isAuthenticated, sendIntent, promptConnect, state } = useWallet();
  const { data: agentsData } = useApi(() => Api.agents(), [sessionReady], { skip: !sessionReady });
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [graph, setGraph] = useState(null);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  const wrapRef = useRef(null);
  const fgRef = useRef(null);
  const stageRef = useRef(null);
  const inputRef = useRef(null);
  const [size, setSize] = useState({ width: 800, height: 520 });
  // Desktop vs mobile: tune spacing/forces and canvas separately for each.
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 861px)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(min-width: 861px)');
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  // Grow the textarea to fit its content so the whole prompt is always visible
  // (no tiny scrolling box). Runs whenever the prompt text changes.
  useEffect(() => {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }, [prompt]);

  // Size the canvas to its container so the map fills the space on any screen.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setSize({ width: Math.max(320, r.width), height: Math.max(360, r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [graph]);

  // Spread the nodes out so they never bunch up: stronger repulsion, longer
  // links, and a collision radius so bubbles + labels keep clear space and
  // never overlap. Nodes only move when you grab one — no hover interaction.
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg || !graph) return;
    // Desktop has room to breathe: push nodes further apart with longer
    // links. Mobile stays tighter so the whole map fits a narrow screen.
    const cfg = isDesktop
      ? { charge: -900, distanceMax: 1100, linkDist: 180, collide: 64 }
      : { charge: -420, distanceMax: 700, linkDist: 110, collide: 46 };
    const charge = fg.d3Force?.('charge');
    if (charge) { charge.strength(cfg.charge); charge.distanceMax(cfg.distanceMax); }
    const link = fg.d3Force?.('link');
    if (link) link.distance(cfg.linkDist).strength(0.5);
    fg.d3Force?.('collide', forceCollide(cfg.collide).strength(0.9).iterations(2));
    fg.d3ReheatSimulation?.();
  }, [graph, isDesktop]);

  const generate = useCallback(async () => {
    const brief = prompt.trim();
    if (!brief || busy || !sessionReady) return;
    setBusy(true);
    setError(null);
    setSelected(null);
    try {
      const res = await Api.map(brief);
      setGraph(res.graph);
      setMeta({ model: res.model, generatedAt: res.generatedAt, count: res.graph?.nodes?.length ?? 0 });
      // The map is free to explore. Recording it on chain is wallet-scoped
      // (no passport required), so any signed-in wallet earns a credit and gets
      // a MetaMask prompt from tap one. The map render never blocks on the wallet.
      if (isAuthenticated) {
        try {
          const { intent } = await Api.walletMapIntent();
          const r = await sendIntent({ intent, txType: 'wallet_map' });
          if (r.status === 'success') {
            pushToast?.({ tone: 'success', message: 'Map recorded on chain.' });
          }
        } catch (err) {
          pushToast?.({ tone: 'danger', message: `Map built. On-chain record failed: ${err.message}` });
        }
      } else {
        // Guest: the map is free, but to record it on chain the user must sign in.
        pushToast?.({ tone: 'info', message: 'Map ready. Connect and sign in to record it on chain and earn a credit.' });
        try { promptConnect?.(); } catch { /* no wallet injected */ }
      }
      // Bring the map into view (it renders below the prompt, off-screen on
      // mobile) and frame the whole graph once physics has settled.
      setTimeout(() => {
        stageRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        fgRef.current?.zoomToFit?.(600, 60);
      }, 400);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }, [prompt, busy, sessionReady, isAuthenticated, sendIntent, agentsData, pushToast, promptConnect, state]);

  const data = useMemo(() => {
    if (!graph) return { nodes: [], links: [] };
    // Clone so force-graph can mutate x/y/vx/vy without touching state.
    return {
      nodes: graph.nodes.map((n) => ({ ...n })),
      links: graph.links.map((l) => ({ ...l })),
    };
  }, [graph]);

  // How many links each node has — drives bubble size (hubs are bigger) and
  // which labels show when zoomed out.
  const degree = useMemo(() => {
    const d = new Map();
    if (graph) {
      for (const l of graph.links) {
        const s = typeof l.source === 'object' ? l.source.id : l.source;
        const t = typeof l.target === 'object' ? l.target.id : l.target;
        d.set(s, (d.get(s) ?? 0) + 1);
        d.set(t, (d.get(t) ?? 0) + 1);
      }
    }
    return d;
  }, [graph]);

  // Neighbours of the selected node, so we can highlight its immediate
  // connections and dim everything else.
  const neighbors = useMemo(() => {
    const set = new Set();
    if (graph && selected) {
      set.add(selected.id);
      for (const l of graph.links) {
        const s = typeof l.source === 'object' ? l.source.id : l.source;
        const t = typeof l.target === 'object' ? l.target.id : l.target;
        if (s === selected.id) set.add(t);
        if (t === selected.id) set.add(s);
      }
    }
    return set;
  }, [graph, selected]);

  // Distinct groups present, with counts — drives the readable colour legend.
  const groups = useMemo(() => {
    const m = new Map();
    if (graph) {
      for (const n of graph.nodes) {
        const g = String(n.group ?? 'other').toLowerCase();
        m.set(g, (m.get(g) ?? 0) + 1);
      }
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [graph]);

  return (
    <div className="stack mapme">
      <JourneySteps current={0} />
      <header className="page-header">
        <div>
          <h1 className="page-title">Map me</h1>
          <p className="page-subtitle">
            Tell Cortis a little about your life or work. It turns it into a living map you can pull apart and explore. Not sure what to write? Tap a starting point below.
          </p>
        </div>
      </header>

      <section className="mapme__prompt">
        <label className="sr-only" htmlFor="mapme-input">Describe yourself</label>
        <div className="mapme__examples" role="group" aria-label="Starting points">
          {EXAMPLE_PROMPTS.map((ex) => (
            <button
              key={ex.label}
              type="button"
              className="mapme__chip"
              onClick={() => setPrompt(ex.text)}
              disabled={busy}
            >
              {ex.label}
            </button>
          ))}
        </div>
        <textarea
          id="mapme-input"
          ref={inputRef}
          className="mapme__input"
          rows={3}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={DEFAULT_PROMPT}
        />
        <div className="mapme__actions">
          <Button variant="primary" loading={busy} onClick={generate} disabled={!prompt.trim()}>
            {graph ? 'Regenerate map' : 'Map me'}
          </Button>
          {meta && (
            <span className="mapme__meta text-muted mono">
              {meta.count} nodes
            </span>
          )}
        </div>
      </section>

      {error && <ErrorState error={error} onRetry={generate} context="The map could not be generated" />}

      {busy && !graph && (
        <div className="mapme__placeholder">
          <p>Building your map…</p>
        </div>
      )}

      {graph && !error && (
        <section className="mapme__stage" ref={stageRef}>
          <div className="mapme__toolbar">
            <div className="mapme__legend">
              {groups.map(([g, n]) => (
                <span key={g} className="mapme__legend-item">
                  <span className="mapme__legend-dot" style={{ background: colorFor(g) }} />
                  {g} <span className="mapme__legend-count">{n}</span>
                </span>
              ))}
            </div>
            <div className="mapme__zoom">
              <button type="button" aria-label="Zoom in" onClick={() => fgRef.current?.zoom?.((fgRef.current?.zoom?.() ?? 1) * 1.4, 300)}>+</button>
              <button type="button" aria-label="Zoom out" onClick={() => fgRef.current?.zoom?.((fgRef.current?.zoom?.() ?? 1) / 1.4, 300)}>−</button>
              <button type="button" aria-label="Fit map" onClick={() => fgRef.current?.zoomToFit?.(500, 50)}>Fit</button>
            </div>
          </div>
          <div className="mapme__canvas" ref={wrapRef}>
            <ForceGraph2D
              ref={fgRef}
              width={size.width}
              height={size.height}
              graphData={data}
              backgroundColor="#08060F"
              cooldownTicks={200}
              warmupTicks={60}
              d3VelocityDecay={0.28}
              d3AlphaDecay={0.018}
              onEngineStop={() => fgRef.current?.zoomToFit?.(700, 50)}
              minZoom={0.4}
              maxZoom={6}
              enableNodeDrag
              onNodeDrag={(node) => {
                node.fx = node.x;
                node.fy = node.y;
              }}
              linkColor={() => 'rgba(155, 133, 255, 0.22)'}
              linkWidth={(l) => (selected && (l.source === selected || l.target === selected || l.source?.id === selected.id || l.target?.id === selected.id) ? 2 : 1)}
              linkDirectionalParticles={2}
              linkDirectionalParticleWidth={(l) => (selected && (l.source?.id === selected.id || l.target?.id === selected.id) ? 3 : 1.6)}
              linkDirectionalParticleSpeed={0.006}
              linkDirectionalParticleColor={() => 'rgba(124, 92, 255, 0.9)'}
              nodeRelSize={6}
              nodeVal={(n) => 1 + (degree.get(n.id) ?? 0)}
              nodeLabel={() => ''}
              onNodeClick={(n) => { setSelected(n); fgRef.current?.centerAt?.(n.x, n.y, 500); }}
              onBackgroundClick={() => setSelected(null)}
              nodeCanvasObject={(node, ctx, scale) => {
                const label = node.label ?? '';
                const deg = degree.get(node.id) ?? 0;
                const isYou = String(node.group ?? '').toLowerCase() === 'you';
                const base = isYou ? 9 : 5 + Math.min(6, deg * 1.4);
                const active = selected && (node === selected || node.id === selected.id);
                const dim = selected && !active && !neighbors.has(node.id);
                const color = colorFor(node.group);
                ctx.globalAlpha = dim ? 0.25 : 1;
                // soft glow
                ctx.beginPath();
                ctx.arc(node.x, node.y, base, 0, 2 * Math.PI, false);
                ctx.fillStyle = color;
                ctx.shadowColor = color;
                ctx.shadowBlur = active ? 26 : 12;
                ctx.fill();
                ctx.shadowBlur = 0;
                if (isYou) {
                  ctx.lineWidth = 2 / scale;
                  ctx.strokeStyle = 'rgba(255,255,255,0.85)';
                  ctx.stroke();
                }
                // With a small, well-spaced map every node stays labelled so
                // the whole picture is readable. On selection, non-neighbours
                // dim but keep their (faded) label for context.
                const showLabel = true;
                if (showLabel) {
                  const fontSize = Math.max((isYou ? 4.4 : 3.6), (isYou ? 13 : 11) / scale);
                  ctx.font = `${isYou ? 700 : 600} ${fontSize}px Inter, system-ui, sans-serif`;
                  ctx.textAlign = 'center';
                  ctx.textBaseline = 'top';
                  const tw = ctx.measureText(label).width;
                  const px = 4 / scale;
                  const py = 2 / scale;
                  const ly = node.y + base + 3 / scale;
                  // rounded dark pill behind text so it stays readable over links
                  const rx = node.x - tw / 2 - px;
                  const rw = tw + px * 2;
                  const rh = fontSize + py * 2;
                  const rr = Math.min(4 / scale, rh / 2);
                  ctx.fillStyle = dim ? 'rgba(8,6,15,0.5)' : 'rgba(8,6,15,0.82)';
                  ctx.beginPath();
                  if (ctx.roundRect) ctx.roundRect(rx, ly - py, rw, rh, rr);
                  else ctx.rect(rx, ly - py, rw, rh);
                  ctx.fill();
                  ctx.fillStyle = dim ? 'rgba(247,244,255,0.45)' : '#F7F4FF';
                  ctx.fillText(label, node.x, ly);
                }
                ctx.globalAlpha = 1;
              }}
              nodePointerAreaPaint={(node, color, ctx) => {
                const deg = degree.get(node.id) ?? 0;
                const isYou = String(node.group ?? '').toLowerCase() === 'you';
                const base = isYou ? 9 : 5 + Math.min(6, deg * 1.4);
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.arc(node.x, node.y, base + 4, 0, 2 * Math.PI, false);
                ctx.fill();
              }}
              onNodeDragEnd={(node) => {
                // Pin the node exactly where it was dropped so it stays put.
                // Other bubbles keep reacting, but this one holds its position.
                node.fx = node.x;
                node.fy = node.y;
              }}
            />
          </div>
          {selected && (
            <aside className="mapme__detail">
              <button type="button" className="mapme__detail-close" onClick={() => setSelected(null)} aria-label="Close">×</button>
              <span className="mapme__detail-group" style={{ color: colorFor(selected.group) }}>{selected.group ?? 'node'}</span>
              <h2 className="mapme__detail-title">{selected.label}</h2>
              {selected.summary && <p className="mapme__detail-summary">{selected.summary}</p>}
              <p className="mapme__detail-hint">{(degree.get(selected.id) ?? 0)} connection{(degree.get(selected.id) ?? 0) === 1 ? '' : 's'}</p>
            </aside>
          )}
        </section>
      )}

      {!graph && !error && (
        <div className="mapme__placeholder">
          <p>Press <strong>Map me</strong> to build your map. Drag any bubble and watch the rest follow.</p>
        </div>
      )}

      <JourneyNext current={0} done={Boolean(graph)} />
    </div>
  );
}
